// libraries
import React, { useState, useEffect, useId } from 'react'
import clsx from 'clsx'
import { useFormContext, RegisterOptions } from 'react-hook-form'

// utils
import { isNonWhitespaceString } from '/lib/reactutils/utils'

// svg
import { Warning, Plus, Close } from '/react4xp/components/Svg/Ux/Ux'

interface UploadProps {
    id: string
    name: string
    label?: string
    uploadButtonText: string
    required?: boolean
    disabled?: boolean
    className?: string
    accept?: string
    multiple?: boolean
    maxFileSize?: number
    helperText?: string
    errorMessages?: {
        required?: string
        maxFileSize?: string
        format?: string
    }
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
}

export default function Upload({
    id,
    name,
    label,
    uploadButtonText,
    required,
    disabled,
    className,
    accept,
    multiple,
    maxFileSize,
    errorMessages,
    helperText,
    onChange
}: UploadProps) {
    const [files, setFiles] = useState<File[]>([])
    const [customErrors, setCustomErrors] = useState<string[]>([])

    // Choosing a second unusable file produces the same message as the first.
    // React leaves the unchanged text alone, so the region would never speak
    // again. Re-keying it on each attempt makes every rejection announce.
    const [attempt, setAttempt] = useState(0)

    const formContext = useFormContext()
    const register = formContext ? formContext.register : () => ({})
    const errors = formContext ? formContext.formState.errors : {}

    let validations: RegisterOptions = {
        //onChange: (e) => onChange(e),
        required
    }

    if (required) {
        validations.required = errorMessages?.required
    }

    const errorMessage = errors && errors[name]?.message

    const showHelperText = isNonWhitespaceString(helperText)
    const instanceId = useId()
    const helperTextId = `${instanceId}-helper-text`
    const errorMessageId = `${instanceId}-error-message`
    const hasError = Boolean(errorMessage) || customErrors.length > 0

    const describedBy = [
        showHelperText && helperTextId,
        hasError && errorMessageId
    ].filter(Boolean).join(' ') || undefined

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files
        const newErrors: string[] = []

        if (selectedFiles) {
            const MAX_FILE_SIZE = maxFileSize ? maxFileSize * 1024 * 1024 : 10 * 1024 * 1024
            const newFiles: File[] = []

            for (let i = 0; i < selectedFiles.length; i++) {
                const file = selectedFiles[i]

                // Check file size
                if (file.size > MAX_FILE_SIZE) {
                    newErrors.push(errorMessages?.maxFileSize || `File size exceeds the ${maxFileSize}MB limit.`)
                    continue
                }

                // Check file format if accept is specified
                if (accept) {
                    const acceptedTypes = accept.split(',').map(type => type.trim())
                    const fileType = file.type
                    const isValidType = acceptedTypes.some(type => {
                        if (type.includes('*')) {
                            return fileType.startsWith(type.replace('*', ''))
                        }
                        return type === fileType
                    })

                    if (!isValidType) {
                        newErrors.push(errorMessages?.format || 'File format not supported.')
                        continue
                    }
                }

                // Only add the file if it's not already in the state
                if (!files.some(existingFile => existingFile.name === file.name)) {
                    newFiles.push(file)
                }
            }

            // Update errors state
            setCustomErrors(newErrors)
            setAttempt(current => current + 1)

            // Only proceed with file upload if there are no errors
            if (newErrors.length === 0) {
                // append the new files to the existing ones
                setFiles(prevFiles => [...prevFiles, ...newFiles])

                const fileInput = document.getElementById(id) as HTMLInputElement
                const newFilesDT = new DataTransfer()
                if (fileInput) {
                    ((files || []).concat(newFiles)).forEach(f => {
                        newFilesDT.items.add(f)
                    })
                    fileInput.files = newFilesDT.files
                }

                if (onChange) {
                    onChange(event)
                }
            }
        }
    }

    const handleRemoveFile = (fileNameToRemove: string) => {
        const updatedFiles = files.filter(file => file.name !== fileNameToRemove)
        setFiles(updatedFiles)

        const fileInput = document.getElementById(id) as HTMLInputElement

        if (fileInput) {
            const newFiles = new DataTransfer()

            Array.from(fileInput.files).forEach((file) => {
                if (file.name != fileNameToRemove) {
                    newFiles.items.add(file)
                }
            })

            fileInput.files = newFiles.files
        }
    }

    useEffect(() => {
        const handleResetForm = () => {
            setFiles([])
        }

        document.addEventListener('resetForm', handleResetForm)

        return () => {
            document.removeEventListener('resetForm', handleResetForm)
        }
    }, [setFiles])

    return (
        <div className={clsx(
            'form-line',
            className,
            (errors && errors[name] || customErrors.length > 0) && 'form-line--error'
        )}>

            <label className='form-line__label text-md' htmlFor={id}>
                {label} {required && <span className='form-line__label__required text-sm'>(Required)</span>}
            </label>

            <div className='form-line__line-wrapper'>

                <input
                    type='file'
                    id={id}
                    name={name}
                    className='form-line__input form-line__input--file'
                    disabled={disabled || false}
                    accept={accept}
                    multiple={multiple}
                    {...(register ? register(name, validations) : {})}
                    onChange={handleFileChange}
                    aria-required={required || undefined}
                    aria-invalid={hasError}
                    aria-describedby={describedBy}
                />

                <label
                    htmlFor={id}
                    className='form-line__button'
                >
                    <Plus />
                    {uploadButtonText}
                </label>

                {showHelperText && (
                    <div
                        id={helperTextId}
                        className='form-line__help-text text-sm'
                        dangerouslySetInnerHTML={{ __html: helperText }}
                        suppressHydrationWarning
                    />
                )}

                <div
                    key={attempt}
                    id={errorMessageId}
                    role='alert'
                    className='form-line__error-messages'
                >
                        {errorMessage && (
                            <p className='
                                form-line__error-message
                                form-line__error-message--input
                                labels-sm-regular
                            '>
                                <Warning />
                                {typeof errorMessage === 'string' && errorMessage}
                            </p>
                        )}
                        {customErrors.map((error, index) => (
                            <p
                                key={index}
                                className='
                                    form-line__error-message
                                    form-line__error-message--input
                                    labels-sm-regular
                                '
                            >
                                <Warning />
                                {error}
                            </p>
                        ))}
                </div>

                {files.length ? (
                    <div className='form-line__file-list'>
                        
                        {files.map((file, i) => (
                            <div
                                className='form-line__remove-file'
                                key={i}
                            >
                                
                                <p className='file-name text-small'>
                                    {file.name}
                                </p>

                                <button
                                    type='button'
                                    onClick={() => handleRemoveFile(file.name)}
                                >
                                    <Close />
                                    Remove
                                </button>

                            </div>
                        ))}
                    </div>
                ) : null}

            </div>

        </div>
    )
}